import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Database, Save, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { getAirtimeNetworkMeta } from '@/constants/airtimeNetworks';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminDataService, getApiErrorMessage } from '@/services';
import type { AdminDataPlan } from '@/types/data';
import type { AirtimeNetwork } from '@/types/airtime';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { notify } from '@/utils/notify';

const NETWORK_ORDER: AirtimeNetwork[] = ['MTN', 'AIRTEL', 'GLO', '9MOBILE'];

interface PriceInputDraft {
  providerCost: string;
  subscriberPrice: string;
  agentPrice: string;
}

function toPriceInputs(plan: AdminDataPlan): PriceInputDraft {
  return {
    providerCost: String(plan.providerCost),
    subscriberPrice: String(plan.subscriberPrice),
    agentPrice: String(plan.agentPrice),
  };
}

function profitPreview(providerCost: number, sellingPrice: number) {
  return Math.round((sellingPrice - providerCost) * 100) / 100;
}

function parsePriceField(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

function formatProfitPreview(costValue: string, sellValue: string) {
  const cost = parsePriceField(costValue);
  const sell = parsePriceField(sellValue);
  if (cost == null || sell == null) return '—';
  return `₦${profitPreview(cost, sell).toFixed(0)}`;
}

function sortNetworks(networks: string[]): AirtimeNetwork[] {
  return NETWORK_ORDER.filter((network) => networks.includes(network));
}

export function AdminDataPlansPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [plans, setPlans] = useState<AdminDataPlan[]>([]);
  const [drafts, setDrafts] = useState<Record<number, AdminDataPlan>>({});
  const [priceInputs, setPriceInputs] = useState<Record<number, PriceInputDraft>>({});
  const [search, setSearch] = useState('');
  const [networkFilter, setNetworkFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [collapsedNetworks, setCollapsedNetworks] = useState<Record<string, boolean>>({});

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminDataService.listPlans();
      const rows = res.data?.plans ?? [];
      setPlans(rows);
      setDrafts(Object.fromEntries(rows.map((row) => [row.id, { ...row }])));
      setPriceInputs(Object.fromEntries(rows.map((row) => [row.id, toPriceInputs(row)])));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load data plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const networks = useMemo(() => sortNetworks([...new Set(plans.map((p) => p.network))]), [plans]);

  const networkStats = useMemo(() => {
    return Object.fromEntries(
      networks.map((network) => {
        const rows = plans.filter((p) => p.network === network);
        return [
          network,
          {
            total: rows.length,
            active: rows.filter((p) => p.isActive).length,
            types: [...new Set(rows.map((p) => p.planType))].length,
          },
        ];
      }),
    );
  }, [networks, plans]);

  const planTypes = useMemo(() => {
    const types = plans
      .filter((p) => networkFilter === 'ALL' || p.network === networkFilter)
      .map((p) => p.planType);
    return [...new Set(types)].sort();
  }, [plans, networkFilter]);

  const filteredPlans = useMemo(() => {
    const query = search.trim().toLowerCase();
    return plans.filter((plan) => {
      if (networkFilter !== 'ALL' && plan.network !== networkFilter) return false;
      if (typeFilter !== 'ALL' && plan.planType !== typeFilter) return false;
      if (!query) return true;
      return (
        plan.planName.toLowerCase().includes(query) ||
        plan.planType.toLowerCase().includes(query) ||
        plan.network.toLowerCase().includes(query) ||
        String(plan.providerPlanId).includes(query) ||
        plan.validity.toLowerCase().includes(query)
      );
    });
  }, [plans, networkFilter, typeFilter, search]);

  const grouped = useMemo(() => {
    const map: Record<string, Record<string, AdminDataPlan[]>> = {};
    for (const plan of filteredPlans) {
      if (!map[plan.network]) map[plan.network] = {};
      if (!map[plan.network]![plan.planType]) map[plan.network]![plan.planType] = [];
      map[plan.network]![plan.planType]!.push(plan);
    }
    return map;
  }, [filteredPlans]);

  const orderedNetworkGroups = useMemo(
    () => sortNetworks(Object.keys(grouped)).map((network) => [network, grouped[network]!] as const),
    [grouped],
  );

  function updateDraft(id: number, patch: Partial<AdminDataPlan>) {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id]!, ...patch },
    }));
  }

  function updatePriceInput(id: number, field: keyof PriceInputDraft, value: string) {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setPriceInputs((prev) => ({
      ...prev,
      [id]: { ...prev[id]!, [field]: value },
    }));
  }

  function toggleNetworkCollapse(network: string) {
    setCollapsedNetworks((prev) => ({ ...prev, [network]: !prev[network] }));
  }

  async function handleSave(plan: AdminDataPlan) {
    const draft = drafts[plan.id];
    const prices = priceInputs[plan.id];
    if (!draft || !prices) return;

    const providerCost = parsePriceField(prices.providerCost);
    const subscriberPrice = parsePriceField(prices.subscriberPrice);
    const agentPrice = parsePriceField(prices.agentPrice);

    if (providerCost == null || subscriberPrice == null || agentPrice == null) {
      notify.error('Cost, subscriber, and agent prices are required');
      return;
    }

    if (subscriberPrice < providerCost || agentPrice < providerCost) {
      notify.error('Selling prices cannot be below provider cost');
      return;
    }

    setSavingId(plan.id);
    try {
      const res = await adminDataService.updatePlan(plan.id, {
        providerCost,
        subscriberPrice,
        agentPrice,
        isActive: draft.isActive,
      });
      const updated = res.data?.plan;
      if (updated) {
        setPlans((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        setDrafts((prev) => ({ ...prev, [updated.id]: updated }));
        setPriceInputs((prev) => ({ ...prev, [updated.id]: toPriceInputs(updated) }));
      }
      notify.success('Plan pricing saved');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to save plan'));
    } finally {
      setSavingId(null);
    }
  }

  const inputClass = cn(
    'w-full rounded-lg border px-2.5 py-2 text-sm focus:outline-none focus:ring-2',
    shell.input,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading data plans" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">Data Plans</h1>
        <p className={cn('mt-1 text-sm sm:text-base', shell.cardMuted)}>
          Plans grouped by network (MTN → Airtel → Glo), then plan type. Set provider cost and fixed
          subscriber/agent prices per plan.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <button
          type="button"
          onClick={() => {
            setNetworkFilter('ALL');
            setTypeFilter('ALL');
          }}
          className={cn(
            'rounded-xl border px-4 py-3 text-left transition',
            networkFilter === 'ALL' ? 'border-secondary bg-secondary/10' : shell.card,
          )}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">All networks</p>
          <p className="mt-1 text-2xl font-bold">{plans.length}</p>
          <p className={cn('text-xs', shell.cardMuted)}>{plans.filter((p) => p.isActive).length} active</p>
        </button>

        {networks.map((network) => {
          const meta = getAirtimeNetworkMeta(network);
          const stats = networkStats[network];
          return (
            <button
              key={network}
              type="button"
              onClick={() => {
                setNetworkFilter(network);
                setTypeFilter('ALL');
              }}
              className={cn(
                'rounded-xl border px-4 py-3 text-left transition',
                networkFilter === network ? 'border-secondary bg-secondary/10' : shell.card,
              )}
            >
              <div className="flex items-center gap-2">
                <img src={meta.logo} alt={meta.label} className="h-7 w-7 rounded bg-white p-0.5 object-contain" />
                <p className="text-xs font-semibold uppercase tracking-wide">{meta.label}</p>
              </div>
              <p className="mt-2 text-2xl font-bold">{stats?.total ?? 0}</p>
              <p className={cn('text-xs', shell.cardMuted)}>
                {stats?.active ?? 0} active · {stats?.types ?? 0} types
              </p>
            </button>
          );
        })}
      </div>

      <Card className={cn('p-4 sm:p-5', shell.card)}>
        <div className="grid gap-3 md:grid-cols-4">
          <div className="relative md:col-span-2">
            <Search className={cn('absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', shell.cardMuted)} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plan, network, type, or provider ID..."
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <select
            value={networkFilter}
            onChange={(e) => {
              setNetworkFilter(e.target.value);
              setTypeFilter('ALL');
            }}
            className={inputClass}
          >
            <option value="ALL">All networks</option>
            {networks.map((network) => (
              <option key={network} value={network}>
                {getAirtimeNetworkMeta(network).label}
              </option>
            ))}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={inputClass}>
            <option value="ALL">All types</option>
            {planTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <p className={cn('mt-3 text-sm', shell.cardMuted)}>
          Showing {filteredPlans.length} of {plans.length} plans
        </p>
      </Card>

      {orderedNetworkGroups.length === 0 ? (
        <Card className={cn('p-8 text-center', shell.card)}>
          <p className={shell.cardMuted}>No plans match your filters.</p>
        </Card>
      ) : (
        orderedNetworkGroups.map(([network, types]) => {
          const meta = getAirtimeNetworkMeta(network as AirtimeNetwork);
          const networkPlans = filteredPlans.filter((p) => p.network === network);
          const isCollapsed = collapsedNetworks[network] ?? false;
          const typeEntries = Object.entries(types).sort(([a], [b]) => a.localeCompare(b));

          return (
            <Card key={network} className={cn('overflow-hidden', shell.card)}>
              <button
                type="button"
                onClick={() => toggleNetworkCollapse(network)}
                className={cn(
                  'flex w-full items-center justify-between gap-4 border-b px-4 py-4 text-left sm:px-6',
                  shell.tableWrap,
                )}
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2 shadow-sm">
                    <img src={meta.logo} alt={meta.label} className="h-10 w-10 object-contain" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-bold sm:text-2xl">{meta.label}</h2>
                      <span className="rounded-full bg-secondary/15 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-secondary">
                        {network}
                      </span>
                    </div>
                    <p className={cn('mt-1 text-sm', shell.cardMuted)}>
                      {networkPlans.length} plans · {typeEntries.length} types ·{' '}
                      {networkPlans.filter((p) => p.isActive).length} active
                    </p>
                  </div>
                </div>
                {isCollapsed ? (
                  <ChevronRight className={cn('h-5 w-5 shrink-0', shell.cardMuted)} />
                ) : (
                  <ChevronDown className={cn('h-5 w-5 shrink-0', shell.cardMuted)} />
                )}
              </button>

              {!isCollapsed && (
                <div className="space-y-4 p-4 sm:p-6">
                  {typeEntries.map(([planType, rows]) => (
                    <div
                      key={`${network}-${planType}`}
                      className={cn('overflow-hidden rounded-xl border', shell.tableWrap)}
                    >
                      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3 sm:px-5">
                        <img
                          src={meta.logo}
                          alt={meta.label}
                          className="h-6 w-6 rounded bg-white p-0.5 object-contain"
                        />
                        <Database className="h-4 w-4 text-secondary" />
                        <span className="font-semibold">{meta.label}</span>
                        <span className={shell.cardMuted}>·</span>
                        <span className="font-semibold">{planType}</span>
                        <span className={cn('text-sm', shell.cardMuted)}>({rows.length} plans)</span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className={cn('text-left text-xs uppercase tracking-wide', shell.cardMuted)}>
                            <tr>
                              <th className="px-4 py-3">Provider ID</th>
                              <th className="px-4 py-3">Plan</th>
                              <th className="px-4 py-3">Validity</th>
                              <th className="px-4 py-3">Cost</th>
                              <th className="px-4 py-3">Subscriber</th>
                              <th className="px-4 py-3">Agent</th>
                              <th className="px-4 py-3">Profit</th>
                              <th className="px-4 py-3">Active</th>
                              <th className="px-4 py-3" />
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((plan) => {
                              const draft = drafts[plan.id] ?? plan;
                              const prices = priceInputs[plan.id] ?? toPriceInputs(plan);
                              const subProfit = formatProfitPreview(prices.providerCost, prices.subscriberPrice);
                              const agentProfit = formatProfitPreview(prices.providerCost, prices.agentPrice);

                              return (
                                <tr key={plan.id} className="border-t border-white/5">
                                  <td className="px-4 py-3 font-mono text-xs">{draft.providerPlanId}</td>
                                  <td className="px-4 py-3 font-medium">{draft.planName}</td>
                                  <td className="px-4 py-3">{draft.validity}</td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={prices.providerCost}
                                      onChange={(e) =>
                                        updatePriceInput(plan.id, 'providerCost', e.target.value)
                                      }
                                      placeholder="Cost"
                                      className={cn(inputClass, 'min-w-[90px]')}
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={prices.subscriberPrice}
                                      onChange={(e) =>
                                        updatePriceInput(plan.id, 'subscriberPrice', e.target.value)
                                      }
                                      placeholder="Subscriber"
                                      className={cn(inputClass, 'min-w-[90px]')}
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={prices.agentPrice}
                                      onChange={(e) =>
                                        updatePriceInput(plan.id, 'agentPrice', e.target.value)
                                      }
                                      placeholder="Agent"
                                      className={cn(inputClass, 'min-w-[90px]')}
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-xs">
                                    <div>Sub: {subProfit}</div>
                                    <div className={shell.cardMuted}>Ag: {agentProfit}</div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="checkbox"
                                      checked={draft.isActive}
                                      onChange={(e) => updateDraft(plan.id, { isActive: e.target.checked })}
                                      className="h-4 w-4 rounded"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <Button
                                      size="sm"
                                      loading={savingId === plan.id}
                                      loadingText="Saving"
                                      onClick={() => void handleSave(plan)}
                                    >
                                      <Save className="h-3.5 w-3.5" />
                                      Save
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}
